// Mentions in Comms messages, kept as markdown-style tokens in the body:
//   @[Ed](member:<uuid>)   #[Grapple rope physics](task:<uuid>)
// The database reads the same format (plain_mentions, messages_sync_mentions),
// so the app, the MCP server and the Slack importer all write it through here.

export const MENTION_ITEM_TYPES = ['task', 'idea', 'doc', 'sprint', 'vault_link', 'file', 'message'] as const;
export type MentionItemType = (typeof MENTION_ITEM_TYPES)[number];

export type Mention =
  { kind: 'member'; id: string; label: string } | { kind: 'item'; type: MentionItemType; id: string; label: string };

const UUID = '[0-9a-fA-F-]{36}';
const TOKEN = new RegExp(`([@#])\\[([^\\]]*)\\]\\((member|${MENTION_ITEM_TYPES.join('|')}):(${UUID})\\)`, 'g');

/** Labels can't hold brackets or line breaks; they'd end the token early. */
function cleanLabel(label: string): string {
  return (
    label
      .replace(/[[\]\r\n]+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim() || 'untitled'
  );
}

export function memberToken(id: string, name: string): string {
  return `@[${cleanLabel(name)}](member:${id})`;
}

export function itemToken(type: MentionItemType, id: string, title: string): string {
  return `#[${cleanLabel(title)}](${type}:${id})`;
}

/** A body cut into plain text and mentions, in order, for rendering. */
export type BodyPart = { text: string } | { mention: Mention };

export function splitMentions(body: string): BodyPart[] {
  const parts: BodyPart[] = [];
  let last = 0;
  for (const m of body.matchAll(TOKEN)) {
    const [whole, sigil, label, type, id] = m as unknown as [string, string, string, string, string];
    if (m.index > last) parts.push({ text: body.slice(last, m.index) });
    parts.push({
      mention:
        sigil === '@' && type === 'member'
          ? { kind: 'member', id, label }
          : { kind: 'item', type: type as MentionItemType, id, label },
    });
    last = m.index + whole.length;
  }
  if (last < body.length) parts.push({ text: body.slice(last) });
  return parts;
}

export function mentionsIn(body: string): Mention[] {
  return splitMentions(body).flatMap((p) => ('mention' in p ? [p.mention] : []));
}

/** The body as people read it: "@Ed can you look at #Grapple rope physics?". */
export function plainMentions(body: string): string {
  return body.replace(TOKEN, (_whole, sigil: string, label: string) => `${sigil}${label}`);
}

/** Whether a message mentions this member. */
export function mentionsMember(body: string, memberId: string): boolean {
  return mentionsIn(body).some((m) => m.kind === 'member' && m.id === memberId);
}

/** A channel name from what someone typed: lower-case, a-z 0-9 - _, at most 80. */
export function channelName(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/^#/, '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/** The body as Claude reads it: "@Ed", and "#Grapple rope physics (task <id>)" so it can follow up. */
export function readableMentions(body: string): string {
  return body.replace(TOKEN, (_whole, sigil: string, label: string, type: string, id: string) =>
    sigil === '@' && type === 'member' ? `@${label}` : `#${label} (${type} ${id})`,
  );
}

const SHORTHAND_ITEM = new RegExp(`(^|[^\\w\\]])#(${MENTION_ITEM_TYPES.join('|')}):(${UUID})`, 'g');

/** The `#<type>:<id>` references in a body written in shorthand (by Claude, or the importer). */
export function shorthandItemRefs(body: string): { type: MentionItemType; id: string }[] {
  return [...body.matchAll(SHORTHAND_ITEM)].map((m) => ({ type: m[2] as MentionItemType, id: m[3]!.toLowerCase() }));
}

/**
 * Turns shorthand into mention tokens: `@Name` (a member's display name or
 * nickname, any case) and `#<type>:<id>`, titled from `titles` (keyed "type:id").
 * Existing tokens are left alone, and an `@` that names nobody stays as text.
 */
export function shorthandToTokens(
  body: string,
  members: readonly { id: string; display_name: string; nickname?: string | null }[],
  titles: ReadonlyMap<string, string>,
): string {
  const items = body.replace(SHORTHAND_ITEM, (_w, before: string, type: MentionItemType, id: string) => {
    const key = `${type}:${id.toLowerCase()}`;
    return `${before}${itemToken(type, id.toLowerCase(), titles.get(key) ?? key)}`;
  });
  if (!members.length) return items;
  // Longest names first, so "@Ed Smith" wins over "@Ed".
  const names = members
    .flatMap((m) => [m.display_name, ...(m.nickname ? [m.nickname] : [])].map((name) => ({ name, member: m })))
    .sort((a, b) => b.name.length - a.name.length);
  const pattern = names.map((n) => n.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const at = new RegExp(`(^|[^\\w@\\]])@(${pattern})(?![\\w])`, 'gi');
  return items.replace(at, (_w, before: string, name: string) => {
    const { member } = names.find((n) => n.name.toLowerCase() === name.toLowerCase())!;
    return `${before}${memberToken(member.id, member.display_name)}`;
  });
}
